import React, { useContext } from 'react';
import { StaticContext } from '@sm/webassets/StaticContext';
import { t, defineMessages } from '@sm/intl';
import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { StaticImageData } from 'next/image';
import HeroFormCTA from '~app/components/HeroFormCTA';
import heroImgV1 from '~static/images/dareToAsk/large-dark-text.webp';
import heroImgV2 from '~static/images/dareToAsk/large-light-text.webp';
import campaign2025Desktop from '~static/images/dareToAsk/campaign-2025-desktop.svg';
import campaign2025Mobile from '~static/images/dareToAsk/campaign-2025-mobile.svg';
import dareToAskUS from '~static/images/dareToAsk/dare-to-ask-us.gif';
import dareToAskUK from '~static/images/dareToAsk/dare-to-ask-uk.gif';
import bwaiGIFTest from '~static/images/bwaiTest/bwai_gif.gif';
import useStyles from './useStyles';
import { CURIOSITY_CON_URL, UK_PATH_LINKS, US_PATH_LINKS } from '../constants';
import { SplitHeroProps } from '~helpers/types';
import SplitHeroImage from './SplitHeroImage';
import SplitHeroCTASection from './SplitHeroCTASection';

export const SPLIT_HERO_COPY = defineMessages({
  BUTTON_LABEL: {
    id: 'SplitHero.ButtonLabel',
    defaultMessage: 'Learn more',
    description: '[Type: label][Vis: high] - The text shown in the pages primary CTA',
  },
  IMAGE_LABEL: {
    id: 'SplitHero.ImageLabel',
    defaultMessage: 'SurveyMonkey Branding',
    description: '[Type: label][Vis: low] - The aria text shown in the pages primary image',
  },
});

const imageMap: { [key: string]: string | StaticImageData } = {
  dareToAskLightText: heroImgV2,
  dareToAskUS,
  dareToAskUK,
  bwaiGIFTest,
  rebrandEP2025: campaign2025Desktop,
  default: heroImgV1,
};

const mobileImageMap: { [key: string]: string | StaticImageData } = {
  rebrandEP2025: campaign2025Mobile,
};

const getImageVariant = (splitHeroImage: string): string | StaticImageData =>
  imageMap[splitHeroImage] || imageMap.default;

const getUrls = (
  isDareToAskCreate: boolean,
  isUkSource: boolean,
  curiosityCon: boolean,
  rebrandEP2025: boolean
): { primaryUrl: string; secondaryUrl: string } => {
  const primaryUrl =
    (curiosityCon && CURIOSITY_CON_URL) ||
    (rebrandEP2025 && '/welcome/get-real-results/') ||
    (isUkSource && UK_PATH_LINKS?.exploreUkCtaUrl) ||
    US_PATH_LINKS.exploreUsCtaUrl ||
    '/mp/dare-to-ask/';

  const secondaryUrl =
    (isDareToAskCreate && '/create/start') || (isUkSource && UK_PATH_LINKS?.ukSignupUrl) || '/sign-up';

  return { primaryUrl, secondaryUrl };
};

const getMobileImageVariant = (splitHeroImage: string): string | StaticImageData =>
  mobileImageMap[splitHeroImage] || '';

const SplitHero = ({
  boldHeader,
  formCopy,
  splitHeroDarkBackground = false,
  splitHeroCTA = false,
  splitHeroPrimaryCTA = '',
  splitHeroSecondaryCTA = '',
  splitHeroImage = '',
  subHeader: subHeaderContent,
  utSource,
  utSource2,
  buildWithAI = false,
  curiosityCon = false,
  rebrandEP2025 = false,
}: SplitHeroProps): React.ReactElement => {
  const classes = useStyles();
  const isUkSource = utSource2 === 'DareToAskPracticalUk';
  const isDareToAskCreate = utSource2 === 'DareToAskCreate';
  const isDareToAskV3SignUp = utSource2 === 'DareToAskV3SignUp';
  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const imageVariant = getImageVariant(splitHeroImage);
  const mobileImageVariant = getMobileImageVariant(splitHeroImage);

  const metricsProps = {
    amplitudeEvent: curiosityCon ? 'experiment event' : 'endpage user interaction',
    templateType,
    surveyId,
    collectorId,
    respondentId,
    utSource,
    utSource2,
    utSource3: 'SplitHero',
  };
  const { primaryUrl, secondaryUrl } = getUrls(isDareToAskCreate, isUkSource, curiosityCon, rebrandEP2025);
  const finalPrimaryUrl = isDareToAskCreate || isDareToAskV3SignUp ? secondaryUrl : primaryUrl;
  const finalSecondaryUrl = isDareToAskCreate || isDareToAskV3SignUp ? primaryUrl : secondaryUrl;
  const primaryCtaText = splitHeroPrimaryCTA || t(SPLIT_HERO_COPY.BUTTON_LABEL);

  const getBackgroundClass = (): string => {
    if (splitHeroDarkBackground) {
      return curiosityCon ? `${classes.darkBackground} ${classes.curiosityConBg}` : classes.darkBackground;
    }
    return '';
  };

  return (
    <Box className={getBackgroundClass()} pt={splitHeroDarkBackground ? 10 : undefined}>
      <Grid noBleeds noGutters>
        <Grid.Item xl={6} lg={6} md={splitHeroCTA ? 12 : 6} sm={12} xs={12}>
          {splitHeroCTA ? (
            <SplitHeroCTASection
              classes={classes}
              curiosityCon={curiosityCon}
              boldHeader={boldHeader}
              subHeaderContent={subHeaderContent}
              splitHeroDarkBackground={splitHeroDarkBackground}
              buildWithAI={buildWithAI}
              primaryUrl={finalPrimaryUrl}
              primaryCtaText={primaryCtaText}
              signUpUrl={finalSecondaryUrl}
              splitHeroSecondaryCTA={splitHeroSecondaryCTA}
              metricsProps={metricsProps}
              rebrandEP2025={rebrandEP2025}
            />
          ) : (
            <Box p={3}>
              <HeroFormCTA className={classes.ctaForm} utSource={utSource} utSource2={utSource2} {...formCopy} />
            </Box>
          )}
        </Grid.Item>
        <Grid.Item xl={6} lg={6} md={splitHeroCTA ? 12 : 6} sm={12} xs={12}>
          <SplitHeroImage
            classes={classes}
            imageVariant={imageVariant}
            mobileImageVariant={mobileImageVariant}
            imageAlt={t(SPLIT_HERO_COPY.IMAGE_LABEL)}
            curiosityCon={curiosityCon}
            {...metricsProps}
          />
        </Grid.Item>
      </Grid>
    </Box>
  );
};

export default SplitHero;
